import '../sprite/ui-sprite';
import { UIElement } from '../ui-element';
import { rebuildChildren, createElement } from '../../global/ui-helpers';
import {
    colorValidator,
    getColorPalette,
    UIIconColors,
    UIIconLayouts,
} from './ui-icon.helpers';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import {
    UIIconColor,
    UIIconGlyph,
    UIIconGlyphRedesign,
    UIIconSize,
} from './ui-icon.types';
import type { UISprite } from '../sprite/ui-sprite';
import type { ElementConfig } from '../ui-element.type';
import styles from './ui-icon.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIIcon
 * @element ui-icon
 * @classdesc Represents a class for <code>ui-icon</code> element.
 * One attribute is mandatory <b>glyph</b> or <b>src</b>.
 * attribute should present one of them. You cannot use both of them.
 * @property {UIIconGlyphRedesign} glyph {@attr glyph} Represents glyph (symbol) icon
 * from library provided by branding.
 * @property {UIIconGlyphRedesign} hoverGlyph {@attr hover-glyph} Represents hover glyph (symbol) icon
 * from library provided by branding.
 * @property {string} src {@attr src} URL to custom icon, can be SVG, PNG, JPEG etc.
 * @property {UIIconColor} [color] {@attr color} Foreground color from library to be set on icon.
 * @property {UIIconColor} [bgcolor] {@attr bgcolor}
 * Background color from library to be set on icon.
 * @property {UIIconSize} [size] {@attr size} Size of the icon
 * @property {UIIconGlyph} [badgeGlyph] {@attr badge-glyph} Glyph (symbol) icon
 * from library provided by branding for badge.
 * @property {HTMLSpanElement} badge Container for badge icon.
 * @example
 * <ui-icon color="turquoise" glyph="calendar"></ui-icon>
 */
@Component({
    tag: 'ui-icon',
    styles,
})
export class UIIcon extends UIElement {
    public static readonly GlyphPrefix = 'ui-glyph-';

    @Attribute(String) glyph!: UIIconGlyphRedesign | UIIconGlyph;
    @Attribute({
        type: String,
        validate: colorValidator,
    })
    bgcolor?: UIIconColor;

    @Attribute({
        type: String,
        validate: colorValidator,
    })
    color?: UIIconColor;
    @Attribute(String) src?: string;
    @Attribute(String) size?: UIIconSize;
    @Attribute(String) badgeGlyph?: UIIconGlyphRedesign;
    @Attribute(String) hoverGlyph?: UIIconGlyphRedesign;
    @Child('.ui-icon__badge') badge!: HTMLSpanElement;

    private static _sprite: UISprite | null;

    /**
     * Possible color value.
     * @deprecated UIIcon.colors is only for backward compatibility and will be removed
     * in future releases. Please use UIIconColors enum instead.
     * @type {Record<string, UIIconColor>}
     */
    static readonly colors = UIIconColors;

    /**
     * @deprecated UIIcon.layouts is only for backward compatibility and will be removed
     * in future releases. Please use UIIconLayouts enum instead.
     * @type {UIIconLayouts}
     */
    static readonly layouts = UIIconLayouts;

    /**
     * @type {Array<string>}
     */
    static get observedAttributes() {
        return ['glyph', 'src', 'badge-glyph', 'bgcolor', 'color'];
    }

    /**
     * Get sprite element
     * @returns {UISprite | null}
     */
    static getSprite(): UISprite | null {
        this._sprite ??= document.querySelector<UISprite>('ui-sprite');
        return this._sprite;
    }

    /**
     * Get ui-icon current layout
     * @returns {string}
     */
    getLayout(): UIIconLayouts {
        if (this.src) {
            return UIIconLayouts.CUSTOM;
        }
        return UIIconLayouts.DEFAULT;
    }

    /**
     * Checks that icon is a custom image from external source.
     * @returns {boolean}
     */
    isCustomImage(): boolean {
        return /(svg|jpg|jpeg|bmp|webp|png)$/i.exec(String(this.src)) !== null;
    }

    /**
     * Build glyph id for svg.
     * @param {UIIconGlyphRedesign} [id]
     * @returns {string}
     */
    buildGlyphId(id?: UIIconGlyphRedesign | UIIconGlyph): string {
        return `#${UIIcon.GlyphPrefix}${id || this.glyph}`;
    }

    /**
     * Build and return config for image element from src attribute.
     * @private
     * @returns {ElementConfig}
     */
    buildImage(): ElementConfig {
        return {
            tagName: 'img',
            attributes: {
                'aria-hidden': 'true',
                'src': this.src,
                'alt': '',
            },
        };
    }

    /**
     * Build and return config for SVG element.
     * @private
     * @param {UIIconGlyphRedesign} [id]
     * @returns {ElementConfig}
     */
    buildSvg(id?: UIIconGlyphRedesign | UIIconGlyph): ElementConfig {
        return {
            tagName: 'svg',
            attributes: {
                'aria-hidden': 'true',
            },
            children: [
                {
                    tagName: 'use',
                    attributes: {
                        'xlink:href': this.buildGlyphId(id),
                    },
                },
            ],
        };
    }

    /**
     * Build and return hover SVG element config.
     * @returns {ElementConfig}
     */
    buildHoverSvg(): ElementConfig {
        return {
            tagName: 'svg',
            classList: { 'ui-icon__hover': true },
            attributes: {
                'aria-hidden': 'true',
            },
            children: [
                {
                    tagName: 'use',
                    attributes: {
                        'xlink:href': this.buildGlyphId(this.hoverGlyph),
                    },
                },
            ],
        };
    }

    /**
     * Build and return badge element config.
     * @returns {ElementConfig}
     */
    buildBadge(): ElementConfig {
        return {
            tagName: 'span',
            classList: {
                'ui-icon__badge': true,
            },
            children: [this.buildSvg(this.badgeGlyph)],
        };
    }

    /**
     * Render or rebuild ui-icon mark up.
     */
    renderIconLayout(): void {
        this.glyph?.split('-')[0] === 'redesign' &&
            this.setAttribute('redesign', '');
        const layout = this.getLayout();
        const children: Array<ElementConfig | Text> = [];
        switch (layout) {
            case UIIconLayouts.CUSTOM:
                if (this.isCustomImage()) {
                    children.push(this.buildImage());
                }
                break;
            default:
                if (this.textContent || this.glyph) {
                    if (this.textContent) {
                        this.glyph ||= this.textContent as UIIconGlyphRedesign;
                        this.innerHTML = '';
                    }
                    children.push(this.buildSvg(this.glyph));
                }
                break;
        }

        if (this.badgeGlyph) {
            children.push(this.buildBadge());
        }

        if (this.hoverGlyph) {
            this.classList.add('-has-hover-glyph');
            children.push(this.buildHoverSvg());
        }

        if (this.size === 'avatar') {
            const { foregroundColor, backgroundColor, warningMessage } =
                getColorPalette(
                    this.bgcolor ?? UIIconColors.DEFAULT,
                    this.color ?? UIIconColors.DEFAULT
                );

            if (UIIcon.DEBUG_MODE && warningMessage) {
                console.warn(warningMessage, this);
            }

            if (foregroundColor) {
                this.color = foregroundColor;
            }

            if (backgroundColor) {
                this.bgcolor = backgroundColor;
            }
        }

        this.rebuildChildren(children);
        this.classList.toggle('-badged', !!this.badgeGlyph);
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        oldValue: string | null,
        newValue: string | null
    ): void {
        switch (name) {
            case 'glyph':
                if (this.querySelector('svg')) {
                    this.renderIconLayout();
                }
                break;
            case 'src':
                if (this.querySelector('img')) {
                    this.renderIconLayout();
                }
                break;
            case 'badge-glyph':
                this.classList.toggle('-badged', !!newValue);

                if (!newValue && this.badge) {
                    this.removeChild(this.badge);
                }
                if (newValue) {
                    let el = this.badge;
                    if (!el) {
                        el = createElement<HTMLSpanElement>({
                            tagName: 'span',
                            classList: {
                                'ui-icon__badge': true,
                            },
                        })!;
                        this.appendChild(el);
                    }
                    const badgeConfig = this.buildBadge();
                    rebuildChildren(this.badge, badgeConfig.children!);
                }
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.renderIconLayout();
    }
}
